import type { GenericFormType } from '@neo/blocks/forms/types'
import { Select, type SelectOption } from '@neo/primitives/select'
import { useCallback, useMemo } from 'react'
import type { SendWhatsappNodeData } from 'v2/pages/flow/types'
import { languageList } from 'v2/pages/settings/whatsapp/templates/create-template.config'
import { useWhatsAppTemplatesQuery } from 'v2/pages/settings/whatsapp/templates/queries'
import type { OutboundWAFormData } from './outbound-wa-form'

interface TemplateSelectProps {
  selectedSender?: string
  form: GenericFormType<OutboundWAFormData | SendWhatsappNodeData['config']>
  showApprovedOnly?: boolean
  disabled?: boolean
}

export const TemplateSelect = ({
  selectedSender,
  form,
  disabled,
  showApprovedOnly,
}: TemplateSelectProps) => {
  const { data: templatesResponse, isLoading } = useWhatsAppTemplatesQuery()

  const countVariables = useCallback((text: string): number => {
    const regex = /{{(\d+)}}/g
    const matches = text.match(regex) || []
    return matches.length
  }, [])
  const isSenderIdNoderVar = selectedSender?.includes('{{')

  const templates = useMemo(() => {
    if (!templatesResponse) return []
    return templatesResponse.flatMap((item) =>
      item.templates_data.waba_templates.filter(
        (t) =>
          t.data.status === 'approved' &&
          (isSenderIdNoderVar
            ? showApprovedOnly
            : selectedSender && item.sender_ids.includes(selectedSender)),
      ),
    )
  }, [templatesResponse, selectedSender, showApprovedOnly, isSenderIdNoderVar])

  const templateOptions: SelectOption[] = useMemo(
    () =>
      templates.map((template) => ({
        label: (
          <div className="flex justify-between items-center w-full gap-2">
            <span className="text-p-400">{template.data.name}</span>
            <span className="text-xs-400 text-secondary">
              ({languageList.find((item) => item.code === template.data.language)?.name})
            </span>
          </div>
        ),
        value: template.uuid,
        searchValue: `${template.data.name} ${
          languageList.find((item) => item.code === template.data.language)?.name
        }`,
      })),
    [templates],
  )

  const handleTemplateChange = (value: string | string[]) => {
    if (typeof value !== 'string') return

    const selectedTemplate = templates.find((t) => t.uuid === value)
    if (!selectedTemplate) return

    if (!showApprovedOnly) form.reset()
    form.setFieldValue('template', selectedTemplate)
    form.validateField('template', 'change')

    const headerComponent = selectedTemplate.data.components.find((c) => c.type === 'HEADER')
    const bodyComponent = selectedTemplate.data.components.find((c) => c.type === 'BODY')
    const hasMedia = headerComponent && headerComponent?.format !== 'TEXT'

    const headerCount = countVariables(headerComponent?.text || '') + (hasMedia ? 1 : 0)
    const bodyCount = countVariables(bodyComponent?.text || '')

    form.setFieldValue('variables.header', Array.from({ length: headerCount }))
    form.setFieldValue('variables.body', Array.from({ length: bodyCount }))
  }

  return (
    <div className="w-full px-0.5">
      <Select
        label="Template"
        placeholder="Choose template"
        options={templateOptions}
        onChange={handleTemplateChange}
        loading={isLoading}
        disabled={disabled}
        value={form.getFieldValue('template')?.uuid}
        data-testid="select-template"
      />
    </div>
  )
}
